import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
    type CreateBannerMutation,
    type CreateBannerMutationVariables,
    CreateBannerDocument,
} from '../../../api/mutations/createBanner';
import { BannerTextPosition } from '../../../api/types';
import useLoadingButton from '../../../components/button/useLoadingButton';
import Form from '../../../components/fields/Form';
import FormAutoTouch from '../../../components/FormAutoTouch';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../utilities/useHandleError';
import { type BannerFormikValue } from '../BannerDetailsPage/BannerDetailsInnerPage';
import BannerForm from '../BannerDetailsPage/BannerForm';
import useUploadBannerImage from '../BannerDetailsPage/useUploadBannerImage';
import useBannerValidator from '../BannerDetailsPage/useValidator';

const initialValues: BannerFormikValue = {
    bannerText: {
        defaultValue: '',
        overrides: [],
    },
    isActive: false,
    bannerTextPosition: BannerTextPosition.Left,
};

const AddBannerPage = () => {
    const { t } = useTranslation(['bannerDetails']);
    const navigate = useNavigate();
    const { setLoading, LoadingButton } = useLoadingButton();

    const handleBannerAsset = useUploadBannerImage();

    const validate = useBannerValidator(false);
    const apolloClient = useApolloClient();

    const onSubmit = useHandleError<BannerFormikValue>(
        async value => {
            setLoading(true);
            // submitting message
            message.loading({
                content: t('eventDetails:messages.createSubmitting'),
                key: 'primary',
                duration: 0,
            });

            const { moduleId, bannerImage, mobileBannerImage, ...settings } = value;
            const { data } = await apolloClient.mutate<CreateBannerMutation, CreateBannerMutationVariables>({
                mutation: CreateBannerDocument,
                variables: {
                    moduleId,
                    settings,
                },
            });

            await handleBannerAsset(data?.banner.id, value);

            // inform about success
            message.success({
                content: t('bannerDetails:messages.createSuccessful'),
                key: 'primary',
            });
            navigate('/admin/banners');
        },
        [apolloClient, handleBannerAsset, navigate, setLoading, t],
        undefined,
        () => {
            setLoading(false);
        }
    );

    return (
        <ConsolePageWithHeader
            footer={[
                <LoadingButton key="submit" form="bannerForm" htmlType="submit" type="primary">
                    {t('bannerDetails:actions.create')}
                </LoadingButton>,
            ]}
            onBack={() => navigate('/admin/banners')}
            title={t('bannerDetails:title')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form id="bannerForm" name="bannerForm" onSubmitCapture={handleSubmit}>
                        <FormAutoTouch />
                        <BannerForm />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default AddBannerPage;
